'use client';

import {
    Alert,
    Box,
    BoxContent,
    Button,
    ButtonList,
    ButtonVariant,
    DirectionEnum,
    FlexBox,
    GapSize,
    RadioTile,
    RadioTiles,
    StatusState,
    Text,
    TextLevels,
    TypographyBold,
    TypographyColor,
} from '@bytel/trilogy-react';

import { EasyTradeInChoice, EasyTradeInResult, EasyTradeInStatus } from '@app-types/easy-trade-in';

import { SmartChangeIcon } from '@components/smart-change';

import { EASY_TRADE_IN_FORM_ID } from '@constants/easy-trade-in';

import { CategoryBox, EasyTradeInConfirmedContent, TradeInBonusInfo } from '@product-detail/components';

export type EasyTradeInBlockProps = {
    choice: EasyTradeInChoice;
    setChoice: (choice: EasyTradeInChoice) => void;
    confirmedResult: EasyTradeInResult | null;
    onOpenModal: () => void;
    showReturnHint?: boolean;
    onReset: () => void;
    tradeInBonus: number | undefined;
    easyTradeInEnabled: boolean;
    hasTradeIn: boolean;
    resetTradeIn: () => void;
};

function EasyTradeInReturnCta({
    onOpenModal,
    tradeInBonus,
}: Readonly<{ onOpenModal: () => void; tradeInBonus?: number }>) {
    return (
        <Box shadowless flat>
            <BoxContent>
                <FlexBox direction={DirectionEnum.COLUMN} gap={GapSize.FIVE}>
                    <FlexBox direction={DirectionEnum.COLUMN} gap={GapSize.FOUR}>
                        <Text level={TextLevels.ONE} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD} marginless>
                            Vous le rendez en bon état, on clôture votre crédit sans frais
                        </Text>
                        <Text level={TextLevels.TWO} marginless>
                            Estimons l&apos;état de votre téléphone. On le reprend même avec des petites rayures.
                        </Text>
                    </FlexBox>
                    <TradeInBonusInfo tradeInBonus={tradeInBonus} />
                </FlexBox>

                <Button variant={ButtonVariant.PRIMARY} onClick={onOpenModal}>
                    Rendre mon téléphone
                </Button>
            </BoxContent>
        </Box>
    );
}

function EasyTradeInConfirmedActions({
    result,
    onOpenModal,
    onReset,
    tradeInBonus,
}: Readonly<{
    result: EasyTradeInResult;
    onOpenModal: () => void;
    onReset: () => void;
    tradeInBonus: number | undefined;
}>) {
    return (
        <>
            <EasyTradeInConfirmedContent result={result} isTradeInConfirmed />
            {result.status !== EasyTradeInStatus.TOO_LOW && <TradeInBonusInfo tradeInBonus={tradeInBonus} />}
            <ButtonList direction={DirectionEnum.COLUMN}>
                <Button variant={ButtonVariant.SECONDARY} onClick={onOpenModal} fullwidth>
                    Modifier ma reprise
                </Button>
                <Button variant={ButtonVariant.GHOST} onClick={onReset} fullwidth>
                    Annuler ma reprise
                </Button>
            </ButtonList>
        </>
    );
}

function EasyTradeInReturnContent({
    easyTradeInEnabled,
    confirmedResult,
    onOpenModal,
    onReset,
    tradeInBonus,
}: Readonly<{
    easyTradeInEnabled: boolean;
    confirmedResult: EasyTradeInResult | null;
    onOpenModal: () => void;
    onReset: () => void;
    tradeInBonus: number | undefined;
}>) {
    if (!easyTradeInEnabled) {
        return (
            <Alert
                display
                status={StatusState.WARNING}
                title='Rendez-vous en boutique pour clôturer votre crédit sans frais'
                description='Finalisez la commande de votre nouveau smartphone dès maintenant et clôturez le crédit de votre téléphone actuel sans frais dans l’une de nos 500 boutiques.'
            />
        );
    }

    if (confirmedResult) {
        return (
            <EasyTradeInConfirmedActions
                result={confirmedResult}
                onOpenModal={onOpenModal}
                onReset={onReset}
                tradeInBonus={tradeInBonus}
            />
        );
    }

    return <EasyTradeInReturnCta onOpenModal={onOpenModal} tradeInBonus={tradeInBonus} />;
}

export function EasyTradeInBlock({
    choice,
    setChoice,
    confirmedResult,
    onOpenModal,
    showReturnHint,
    onReset,
    tradeInBonus,
    easyTradeInEnabled,
    hasTradeIn,
    resetTradeIn,
}: Readonly<EasyTradeInBlockProps>) {
    const isReturningPhone = choice === EasyTradeInChoice.RETURN;
    const isKeepingPhone = choice === EasyTradeInChoice.KEEP;
    const hasConfirmedResult = confirmedResult !== null;

    return (
        <CategoryBox title='Vous voulez conserver votre téléphone actuel&nbsp;?' formId={EASY_TRADE_IN_FORM_ID}>
            <FlexBox direction={DirectionEnum.COLUMN} gap={GapSize.FIVE}>
                <div className='has-text-centered'>
                    <SmartChangeIcon height={24} className='is-hidden-mobile' />
                    <SmartChangeIcon height={32} className='is-hidden-tablet' />
                </div>
                {isReturningPhone && easyTradeInEnabled && showReturnHint && !hasConfirmedResult && (
                    <Text level={TextLevels.THREE} typo={TypographyColor.TEXT_ERROR} marginless>
                        Finalisez la reprise de votre téléphone pour pouvoir l&apos;ajouter au panier.
                    </Text>
                )}
                {!hasConfirmedResult && (
                    <RadioTiles>
                        <RadioTile
                            id='easyTradeIn-return'
                            name='easyTradeInChoice'
                            horizontal
                            checked={isReturningPhone}
                            onChange={() => {
                                if (hasTradeIn) {
                                    resetTradeIn();
                                }
                                setChoice(EasyTradeInChoice.RETURN);
                            }}
                            label='Rendre mon téléphone actuel'
                        />
                        <RadioTile
                            id='easyTradeIn-keep'
                            name='easyTradeInChoice'
                            horizontal
                            checked={isKeepingPhone}
                            onChange={() => setChoice(EasyTradeInChoice.KEEP)}
                            label='Conserver mon téléphone actuel'
                        />
                    </RadioTiles>
                )}
                {isReturningPhone && (
                    <EasyTradeInReturnContent
                        easyTradeInEnabled={easyTradeInEnabled}
                        confirmedResult={confirmedResult}
                        onOpenModal={onOpenModal}
                        onReset={onReset}
                        tradeInBonus={tradeInBonus}
                    />
                )}

                {isKeepingPhone && (
                    <>
                        <Alert
                            className='is-fullwidth'
                            display
                            status={StatusState.INFO}
                            title='Le crédit continuera à être prélevé sur votre compte'
                            description={
                                confirmedResult?.status === EasyTradeInStatus.TOO_LOW
                                    ? "Malheureusement, l'état indiqué pour votre téléphone ne nous permet pas de le reprendre."
                                    : undefined
                            }
                        />
                        {hasConfirmedResult && (
                            <EasyTradeInConfirmedActions
                                result={confirmedResult}
                                onOpenModal={onOpenModal}
                                onReset={onReset}
                                tradeInBonus={tradeInBonus}
                            />
                        )}
                    </>
                )}
            </FlexBox>
        </CategoryBox>
    );
}
